<script setup lang="ts">
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import TagInput from '@/components/entity/TagInput.vue';
import { ArrowRight, FolderPlus } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { buildQuickProjectPayload, canQuickCreateProject, type ProjectCreationDraft } from '~/lib/projectCreation';
import { getTenantProjectStatuses } from '~/lib/projectConfig';

const props = defineProps<{
  open: boolean;
  initialClientId?: string;
}>();

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void;
  (event: 'created', projectId: string): void;
  (event: 'guided', draft: ProjectCreationDraft): void;
}>();

const clientStore = useClientStore();
const projectStore = useProjectStore();
const entityTagsStore = useEntityTagsStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const saving = ref(false);
const draft = ref<ProjectCreationDraft>(emptyDraft());

const clientOptions = computed(() => clientStore.clients.map((client) => ({
  value: client.id,
  label: client.name || client.companyName || client.id,
})));
const statusOptions = computed(() => getTenantProjectStatuses(tenantStore.currentTenant).map((status) => ({
  value: status.value,
  label: status.label,
})));
const canCreate = computed(() => canQuickCreateProject(draft.value) && !saving.value);
const tagSuggestions = computed(() => entityTagsStore.allTags);

function emptyDraft(): ProjectCreationDraft {
  return {
    name: '',
    clientId: props.initialClientId || '',
    description: '',
    status: 'Planning',
    dueDate: '',
    stage: '',
    tags: [],
  };
}

function resetDraft() {
  draft.value = emptyDraft();
}

async function ensureClientsLoaded() {
  if (clientStore.clients.length === 0) await clientStore.fetchClients();
}

async function createProject() {
  if (!canCreate.value) return;
  saving.value = true;
  try {
    const projectId = await projectStore.addProject(
      buildQuickProjectPayload(draft.value),
      userStore.userId || 'unknown',
    );
    try {
      await entityTagsStore.setEntityTags('project', projectId, draft.value.tags || []);
    } catch {
      toast.warning('Project created, but its tags could not be saved.');
    }
    toast.success('Project created.');
    resetDraft();
    emit('update:open', false);
    emit('created', projectId);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Project could not be created.');
  } finally {
    saving.value = false;
  }
}

function continueGuided() {
  emit('guided', { ...draft.value });
  emit('update:open', false);
  resetDraft();
}

watch(() => props.open, (open) => {
  if (open) {
    void ensureClientsLoaded();
    void entityTagsStore.fetchEntityTags();
  }
});

watch(() => props.initialClientId, (clientId) => {
  if (clientId && !draft.value.clientId) draft.value.clientId = clientId;
});
</script>

<template>
  <Sheet :open="open" @update:open="emit('update:open', $event)">
    <SheetContent class="w-full gap-0 p-0 sm:max-w-xl">
      <SheetHeader class="border-b px-6 py-5 text-left">
        <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <FolderPlus class="size-4" />
          Quick create
        </div>
        <SheetTitle>New project</SheetTitle>
        <SheetDescription>Create a blank project now, or carry these details into guided setup.</SheetDescription>
      </SheetHeader>

      <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
        <div class="space-y-2">
          <Label for="quick-project-name">Project name</Label>
          <Input id="quick-project-name" v-model="draft.name" placeholder="Website launch" autofocus />
        </div>

        <div class="space-y-2">
          <Label>Client</Label>
          <SearchableSelectMenu
            v-model="draft.clientId"
            :options="clientOptions"
            placeholder="Choose a client"
            search-placeholder="Search clients..."
            show-all-by-default
            variant="form"
          />
        </div>

        <div class="space-y-2">
          <Label for="quick-project-description">Short description</Label>
          <Textarea id="quick-project-description" v-model="draft.description" rows="4" placeholder="What is this project for?" />
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-2">
            <Label>Status</Label>
            <SearchableSelectMenu
              v-model="draft.status"
              :options="statusOptions"
              placeholder="Choose a status"
              show-all-by-default
              variant="form"
            />
          </div>
          <div class="space-y-2">
            <Label>Due date</Label>
            <DatePicker v-model="draft.dueDate" placeholder="No due date" />
          </div>
        </div>

        <div class="space-y-2">
          <Label for="quick-project-milestone">Starting milestone</Label>
          <Input id="quick-project-milestone" v-model="draft.stage" placeholder="Optional, for example Discovery" />
          <p class="text-xs text-muted-foreground">Leave blank to start with the default Design, Development, and Signoff milestones.</p>
        </div>

        <div class="space-y-2">
          <Label>Tags</Label>
          <TagInput v-model="draft.tags" :suggestions="tagSuggestions" placeholder="For example Retainer or Urgent" />
          <p class="text-xs text-muted-foreground">Internal labels help your team organise and filter projects.</p>
        </div>
      </div>

      <SheetFooter class="sheet-safe-footer border-t px-6 pt-4 sm:justify-between">
        <Button type="button" variant="outline" :disabled="saving" @click="continueGuided">
          Guided setup
          <ArrowRight class="ml-2 size-4" />
        </Button>
        <Button type="button" :disabled="!canCreate" @click="createProject">
          {{ saving ? 'Creating...' : 'Create project' }}
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
